import axios, { AxiosInstance, AxiosResponse } from 'axios';

export type PixKeyType = 'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'EVP';

export interface PaymentModuleConfig {
  enabled: boolean;
  pixKey?: string;
  pixKeyType?: PixKeyType;
  marketplace?: MarketplaceIntegrationConfig;
}

export type MarketplaceStatus = 'NOT_STARTED' | 'PENDING' | 'AUTHORIZED' | 'ERROR' | 'EXPIRED';

export interface MarketplaceIntegrationConfig {
  status: MarketplaceStatus;
  collectorId?: string;
  sellerUserId?: string;
  sellerNickname?: string;
  sellerEmail?: string;
  applicationFeePercent?: number;
  fixedFeeAmount?: number;
  linkedAt?: string;
  lastAttemptAt?: string;
  lastError?: string;
  errorCount?: number;
  credentialMp?: {
    expiresAt?: string;
    scope?: string;
    lastRefreshAt?: string;
    lastError?: string;
  };
}

export interface InventoryModuleConfig {
  enabled: boolean;
}

export interface CompanyModulesConfig {
  payments?: PaymentModuleConfig;
  inventory?: InventoryModuleConfig;
  loyalty?: LoyaltyModuleConfig;
}

export interface LoyaltyModuleConfig {
  enabled: boolean;
  timezone?: string;
  defaultTriggerHour?: string;
  templates?: LoyaltyTemplatesConfig;
}

export interface LoyaltyTemplatesConfig {
  welcomeTemplate?: string;
  abandonedCartTemplate?: string;
  reactivationTemplate?: string;
  launchTemplate?: string;
  progressTemplate?: string;
}

export type CompanyPlan = 'VITRINE_APP' | 'GESTAO_PLUS' | 'WHITE_LABEL_BUSINESS' | 'FULL_CONTROL_ENTERPRISE';

export interface CompanyConfig {
  id: string;
  active: boolean;
  type: string;
  cnpj?: string;
  razaoSocial?: string;
  slug?: string;
  customerPortalUrl?: string;
  whatsappPhone?: string;
  endereco?: {
    cidade: string;
    estado: string;
    complemento?: string;
    numero: string;
    bairro: string;
    rua: string;
    cep: string;
  };
  apiKeyHash?: string;
  hashSecret?: string;
  message?: string;
  isOpen?: boolean;
  dataInicioUsoPlataforma?: string;
  diasFuncionamento?: number[];
  logoPath?: string;
  logoUrl?: string;
  modules?: CompanyModulesConfig;
  plan?: CompanyPlan;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCompanyRequest {
  cnpj: string;
  razaoSocial?: string;
  slug?: string;
  customerPortalUrl?: string;
  whatsappPhone?: string;
  endereco?: {
    cidade: string;
    estado: string;
    complemento?: string;
    numero: string;
    bairro: string;
    rua: string;
    cep: string;
  };
  message?: string;
  dataInicioUsoPlataforma?: string;
  diasFuncionamento?: number[];
  modules?: CompanyModulesConfig;
  plan?: CompanyPlan;
}

class CompanyConfigService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
      timeout: 10000,
    });

    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  async createCompanyWithAdmin(data: CreateCompanyRequest): Promise<CompanyConfig> {
    try {
      const response: AxiosResponse<{ company: CompanyConfig }> = await this.client.post('/admin/companies', data);
      return response.data.company;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao cadastrar empresa');
    }
  }

  async getMyConfig(): Promise<CompanyConfig | null> {
    try {
      const response: AxiosResponse<CompanyConfig | null> = await this.client.get('/admin/company-config/me');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao buscar configuração da empresa');
    }
  }

  async updateMyConfig(
    data: Partial<
      Pick<
        CompanyConfig,
        |
          'isOpen'
          | 'active'
          | 'endereco'
          | 'message'
          | 'dataInicioUsoPlataforma'
          | 'diasFuncionamento'
          | 'razaoSocial'
          | 'slug'
          | 'customerPortalUrl'
          | 'whatsappPhone'
          | 'plan'
          | 'modules'
      >
    > &
      {
        modules?: CompanyModulesConfig;
      }
  ): Promise<CompanyConfig> {
    try {
      const response: AxiosResponse<CompanyConfig> = await this.client.put('/admin/company-config/me', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao atualizar configuração da empresa');
    }
  }

  async uploadLogo(file: File): Promise<{ logoPath?: string; logoUrl?: string }> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response: AxiosResponse<{ logoPath?: string; logoUrl?: string }> = await this.client.post(
        '/admin/company-config/logo',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao fazer upload do logo');
    }
  }
}

export const companyConfigService = new CompanyConfigService();
export default companyConfigService;
